import * as React from "react";
import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Modal as MuiModal,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { formatMonetaryValue, formatMonetaryInput } from "../utils/MonetaryFormatHelper";
import { useFlash } from "../FlashContext";

interface Props {
  showAddNdcChargeModal: boolean;
  closeAddNdcChargeModal: () => void;
  selectedNdcCharge: any;
  setRefreshNdcCharges: any;
}

const MAX_DRUG_CODE_LENGTH = 11;
const MAX_DRUG_DESCRIPTION_LENGTH = 200;
const CHARGE_AMOUNT_MAX_LENGTH = 10

const AddNdcCharge: React.FC<Props> = ({
  showAddNdcChargeModal,
  closeAddNdcChargeModal,
  selectedNdcCharge,
  setRefreshNdcCharges,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [drugCode, setDrugCode] = React.useState(
    selectedNdcCharge?.drug_code ?? ""
  );
  const [drugCodeDescription, setDrugCodeDescription] = React.useState(
    selectedNdcCharge?.drug_code_description ?? ""
  );
  const [chargeAmount, setChargeAmount] = React.useState(
    selectedNdcCharge?.charge_amount ?? ""
  );
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async () => {
    if (!drugCode || drugCode.length !== 11) {
      showToast({
        title: "Failed to Save NDC Charge",
        text: "Please enter 11 digit Drug Code",
        type: "error",
      });
    } else if (!chargeAmount) {
      showToast({
        title: "Failed to Save NDC Charge",
        text: "Please enter Charge Amount",
        type: "error",
      });
    } else {
      setLoading(true);

      const normalizeForSave = (value?: string): string => {
        return value
          ?.trim()
          .replace(/\s+/g, " ")
          ?? "";
      };
      
      const ndc_charge = {
        drug_code: drugCode,
        drug_code_description: normalizeForSave(drugCodeDescription),
        charge_amount: formatMonetaryValue(chargeAmount),
      };

      const url = selectedNdcCharge
        ? `/ndc_charges/${selectedNdcCharge.id}`
        : "/ndc_charges";

      const method = selectedNdcCharge ? "PUT" : "POST";

      try {
        const response = await fetch(url, {
          method,
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({ ndc_charge }),
        });
        const result = await response.json();
        if (result.success) {
          showToast({
            title: "NDC Charge Saved Successfully",
            text: `NDC Charge ${
              selectedNdcCharge ? "updated" : "created"
            } successfully`,
            type: "success",
          });
          setRefreshNdcCharges((prev) => !prev);
          closeAddNdcChargeModal();
        } else {
          showToast({
            title: "Failed to Save NDC Charge",
            text: result.message || "Failed to save NDC Charge",
            type: "error",
          });
        }
      } catch (error) {
        showToast({
          title: "Failed to Save NDC Charge",
          text: error.message ?? "Failed to save NDC Charge",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <MuiModal
      open={showAddNdcChargeModal}
      className="modal-primary"
      onClose={closeAddNdcChargeModal}
    >
      <div className="paper" style={{ width: 430 }}>
        <div className="paperInner">
          <Grid container>
            <Grid item xs={12}>
              <div className="main-header">
                <span>NDC Charge</span>
                <span id="dismiss-button" onClick={closeAddNdcChargeModal}>
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "24px",
                paddingRight: "24px",
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="drug_code" className="field-label">
                    Drug Code*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="drug_code"
                    size="small"
                    value={drugCode ?? ""}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="XXXXXXXXXXX"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const sanitizedValue = event.target.value
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .slice(0, MAX_DRUG_CODE_LENGTH);

                      setDrugCode(sanitizedValue);
                    }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                    slotProps={{
                      htmlInput: { inputMode: "text", maxLength: MAX_DRUG_CODE_LENGTH },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel
                    htmlFor="drug_code_description"
                    className="field-label"
                  >
                    Drug Code Description
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="drug_code_description"
                    size="small"
                    value={drugCodeDescription}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="Enter Description"
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value.length <= MAX_DRUG_DESCRIPTION_LENGTH) {
                        setDrugCodeDescription(value);
                      }
                    }}
                    inputProps={{ maxLength: MAX_DRUG_DESCRIPTION_LENGTH }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="charge_amount" className="field-label">
                    Charge Amount*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="charge_amount"
                    size="small"
                    value={chargeAmount}
                    variant="outlined"
                    className="the-field"
                    placeholder="####.##"
                    required
                    autoComplete="off"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const rawValue = e.target.value;
                      const digitCount = rawValue.replace(/\D/g, "").length;
                      if (digitCount > CHARGE_AMOUNT_MAX_LENGTH) return;

                      const formatted = formatMonetaryInput(rawValue);
                      if (formatted !== null) setChargeAmount(formatted);
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      const formatted = formatMonetaryValue(e.target.value);
                      if (formatted !== "") setChargeAmount(formatted);
                    }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& .MuiInputBase-input": {
                        paddingLeft: chargeAmount ? "0px !important" : undefined,
                      },
                      "& .MuiOutlinedInput-root": {
                        paddingLeft: 0,
                      },
                    }}
                    slotProps={{
                      input: {
                        inputMode: "numeric",
                        startAdornment: chargeAmount ? (
                          <InputAdornment
                            position="start"
                            sx={{ marginRight: "0px", paddingLeft: "10.5px" }}
                          >
                            <Typography sx={{ fontSize: "14px" }}>$</Typography>
                          </InputAdornment>
                        ) : undefined,
                      },
                      htmlInput: {
                        maxLength: CHARGE_AMOUNT_MAX_LENGTH,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="modal-footer"
          >
            <Grid item xs={12} className="confirm-btn-container">
              <div onClick={closeAddNdcChargeModal} className="cancel-link">
                Cancel
              </div>
              <Button
                onClick={handleSubmit}
                className="confirm-btn"
                style={{ width: 156 }}
              >
                {selectedNdcCharge ? "Update" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </div>
        <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </MuiModal>
  );
};

export default AddNdcCharge;
